/**
 * @description
 * Client overview tab component showing client information, deployment readiness,
 * deployment status, and local deployment controls.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 */

import { Link } from 'react-router';
import { CheckCircle, AlertCircle, ExternalLink, Database } from 'lucide-react';
import type { ClientOverviewProps } from './types';

export function ClientOverview({
  client,
  readiness,
  readinessLoading,
  latestDeployment,
  deploymentsLoading,
  deploymentStatus,
  loadingDeploymentStatus,
  onProvisionDatabase,
  onStartLocalDeployment,
  onStopDeployment,
  provisioningDatabase,
  deploying,
}: ClientOverviewProps) {
  const getReadinessColor = (readinessScore: number) => {
    if (readinessScore >= 90) return 'bg-green-100 text-green-800';
    if (readinessScore >= 70) return 'bg-yellow-100 text-yellow-800';
    if (readinessScore >= 50) return 'bg-orange-100 text-orange-800';
    return 'bg-red-100 text-red-800';
  };

  const getReadinessLabel = (readinessScore: number) => {
    if (readinessScore >= 90) return 'Ready';
    if (readinessScore >= 70) return 'Almost Ready';
    if (readinessScore >= 50) return 'Needs Config';
    return 'Not Ready';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
      case 'suspended':
        return 'bg-gray-100 text-gray-800';
      case 'deploying':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="grid gap-6 md:grid-cols-2">
      {/* Client Information */}
      <div className="rounded-lg border bg-card p-6">
        <h2 className="text-lg font-semibold mb-4">Client Information</h2>
        <dl className="space-y-3">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Client ID</dt>
            <dd className="mt-1 text-sm font-mono">{client.id}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Subdomain</dt>
            <dd className="mt-1 text-sm">{client.subdomain}.byteable.ai</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Status</dt>
            <dd className="mt-1">
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(client.status)}`}>
                {client.status}
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Worker ID</dt>
            <dd className="mt-1 text-sm font-mono">{client.workerId || 'Not deployed'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Database ID</dt>
            <dd className="mt-1">
              <div className="flex items-center gap-2">
                {client.databaseId ? (
                  <>
                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <code className="flex-1 text-sm font-mono break-all">{client.databaseId}</code>
                    <a
                      href={`https://dash.cloudflare.com/${client.databaseId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline whitespace-nowrap flex items-center gap-1"
                    >
                      View in Cloudflare
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-4 w-4 text-orange-600 flex-shrink-0" />
                    <span className="flex-1 text-sm text-muted-foreground">Not provisioned</span>
                    <button
                      onClick={onProvisionDatabase}
                      disabled={provisioningDatabase}
                      className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                    >
                      {provisioningDatabase ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                          Provisioning...
                        </>
                      ) : (
                        <>
                          <Database className="h-4 w-4" />
                          Provision Database
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">KV Namespace ID</dt>
            <dd className="mt-1 text-sm font-mono">{client.kvNamespaceId || 'Not provisioned'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Created</dt>
            <dd className="mt-1 text-sm">
              {client.createdAt ? new Date(client.createdAt).toLocaleString() : 'Unknown'}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Last Updated</dt>
            <dd className="mt-1 text-sm">
              {client.updatedAt ? new Date(client.updatedAt).toLocaleString() : 'Unknown'}
            </dd>
          </div>
          {client.lastDeployedAt && (
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Last Deployed</dt>
              <dd className="mt-1 text-sm">
                {client.lastDeployedAt ? new Date(client.lastDeployedAt).toLocaleString() : 'Never'}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* Deployment Readiness */}
      <div className="rounded-lg border bg-card p-6">
        <h2 className="text-lg font-semibold mb-4">Deployment Readiness</h2>
        {readinessLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
          </div>
        ) : readiness ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Readiness Score</span>
              <div className="flex items-center gap-2">
                <div className="w-20 bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      readiness.readinessScore >= 90 ? 'bg-green-500' :
                      readiness.readinessScore >= 70 ? 'bg-yellow-500' :
                      readiness.readinessScore >= 50 ? 'bg-orange-500' :
                      'bg-red-500'
                    }`}
                    style={{ width: `${readiness.readinessScore}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium">{readiness.readinessScore}%</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Status</span>
              <span className={`px-2 py-1 text-xs rounded-full ${getReadinessColor(readiness.readinessScore)}`}>
                {getReadinessLabel(readiness.readinessScore)}
              </span>
            </div>
            {readiness.missingRequired.length > 0 && (
              <div>
                <span className="text-sm font-medium text-red-600">Missing Required:</span>
                <ul className="mt-1 text-sm text-red-600">
                  {readiness.missingRequired.map((item, index) => (
                    <li key={index} className="ml-4">• {item}</li>
                  ))}
                </ul>
              </div>
            )}
            {readiness.missingOptional.length > 0 && (
              <div>
                <span className="text-sm font-medium text-yellow-600">Missing Optional:</span>
                <ul className="mt-1 text-sm text-yellow-600">
                  {readiness.missingOptional.map((item, index) => (
                    <li key={index} className="ml-4">• {item}</li>
                  ))}
                </ul>
              </div>
            )}
            {readiness.recommendations.length > 0 && (
              <div>
                <span className="text-sm font-medium text-blue-600">Recommendations:</span>
                <ul className="mt-1 text-sm text-blue-600">
                  {readiness.recommendations.map((item, index) => (
                    <li key={index} className="ml-4">• {item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Unable to load deployment readiness
          </div>
        )}
      </div>

      {/* Deployment Status */}
      <div className="rounded-lg border bg-card p-6">
        <h2 className="text-lg font-semibold mb-4">Deployment Status</h2>
        {deploymentsLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
          </div>
        ) : latestDeployment ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Current Version</span>
              <span className="text-sm font-mono">{latestDeployment.version}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Last Deployment</span>
              <span className="text-sm">
                {latestDeployment.startedAt ? new Date(latestDeployment.startedAt).toLocaleString() : 'Unknown'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Status</span>
              <span className="flex items-center gap-2 text-sm">
                <span className={`h-2 w-2 rounded-full ${
                  latestDeployment.status === 'success' ? 'bg-green-600' :
                  latestDeployment.status === 'failed' ? 'bg-red-600' :
                  latestDeployment.status === 'deploying' ? 'bg-blue-600 animate-pulse' :
                  'bg-yellow-600'
                }`}></span>
                {latestDeployment.status}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Deployed By</span>
              <span className="text-sm">{latestDeployment.deployedBy}</span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-4">No deployments yet</p>
        )}
      </div>

      {/* Database Information Card */}
      {client.databaseId && (
        <div className="rounded-lg border bg-card p-6">
          <h2 className="text-lg font-semibold mb-4">Database Information</h2>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Database Name</span>
              <code className="font-mono">vibesdk-{client.subdomain}-db</code>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Database ID</span>
              <code className="font-mono text-xs">{client.databaseId}</code>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Schema Version</span>
              <span>1</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Status</span>
              <span className="inline-flex items-center gap-1 text-green-600">
                <CheckCircle className="h-3 w-3" />
                Ready
              </span>
            </div>
            <div className="pt-3 border-t">
              <a
                href={`https://dash.cloudflare.com/${client.databaseId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                <ExternalLink className="h-3 w-3" />
                View in Cloudflare Dashboard
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Local Deployment Section */}
      <div className="rounded-lg border bg-card p-6">
        <h2 className="text-lg font-semibold mb-4">Local Deployment</h2>
        {loadingDeploymentStatus ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
          </div>
        ) : deploymentStatus ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground">Status:</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    deploymentStatus.status === 'running' ? 'bg-green-100 text-green-800' :
                    deploymentStatus.status === 'starting' ? 'bg-blue-100 text-blue-800' :
                    deploymentStatus.status === 'stopped' ? 'bg-gray-100 text-gray-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {deploymentStatus.status}
                  </span>
                </div>
                {deploymentStatus.port && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground">Port:</span>
                    <code className="text-sm">{deploymentStatus.port}</code>
                  </div>
                )}
                {deploymentStatus.pid && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground">PID:</span>
                    <code className="text-sm">{deploymentStatus.pid}</code>
                  </div>
                )}
                {deploymentStatus.port && (
                  <a
                    href={`http://localhost:${deploymentStatus.port}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    Open in browser
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
              <button
                onClick={onStopDeployment}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Stop Deployment
              </button>
            </div>
            <Link
              to={`/deployment-details/${client.id}`}
              className="text-sm text-primary hover:underline"
            >
              View Real-time Logs →
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              No local deployment running. Start a local deployment to test your configuration.
            </p>
            <button
              onClick={onStartLocalDeployment}
              disabled={deploying || !client.databaseId}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deploying ? 'Starting...' : 'Start Local Deployment'}
            </button>
            {!client.databaseId && (
              <p className="text-xs text-orange-600">
                ⚠️ Database must be provisioned before starting local deployment
              </p>
            )}
          </div>
        )}
      </div>

      {/* Production Deployment Section */}
      <div className="rounded-lg border bg-card p-6">
        <h2 className="text-lg font-semibold mb-4">Production Deployment</h2>
        {loadingDeploymentStatus ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Deploy this client to production on Cloudflare Pages to make it publicly accessible.
            </p>
            <button
              onClick={async () => {
                if (!confirm('Deploy to production? This will make the application publicly accessible at a custom domain.')) {
                  return;
                }
                try {
                  // This would be handled by the parent component
                  console.log('Production deployment requested');
                } catch (err) {
                  console.error('Failed to deploy to production:', err);
                }
              }}
              disabled={deploying || !client.databaseId}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deploying ? 'Deploying...' : 'Deploy to Production'}
            </button>
            {!client.databaseId && (
              <p className="text-xs text-orange-600">
                ⚠️ Database must be provisioned before production deployment
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
